import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line

const HEADER_OFFSET_MOBILE = 92; // ticker + header stack
const HEADER_OFFSET_DESKTOP = 100;

/* —— 轮播容器：整屏横滑 + 吸附 —— */
const SlidesWrap = styled.div(({ $snapOff, $dragging }) => [
    tw`absolute inset-0 overflow-x-auto`,
    css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 100%;
    scroll-snap-type: ${$snapOff ? "none" : "x mandatory"};
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
    @media (pointer: fine) {
        cursor: ${$dragging ? "grabbing" : "grab"};
    }
    user-select: none;
  `,
]);

const Container = styled.div`
    ${tw`relative -mx-8 -mt-8 bg-center bg-cover overflow-hidden`}
    height: calc(100vh - ${HEADER_OFFSET_MOBILE}px);
    min-height: calc(100vh - ${HEADER_OFFSET_MOBILE}px);

    @media (min-width: 768px) {
        height: calc(100vh - ${HEADER_OFFSET_DESKTOP}px);
        min-height: calc(100vh - ${HEADER_OFFSET_DESKTOP}px);
    }

    @media (max-height: 720px) {
        min-height: 520px;
    }
`;

/* 每一页作为独立背景层 */
const Slide = styled.div(({ $img }) => [
    tw`relative bg-center bg-cover`,
    css`
        scroll-snap-align: start;
        ${$img ? `background-image: url('${$img}');` : "background-image: none;"}
        background-color: ${$img ? "transparent" : "#000"};
    `,
]);

const VideoBackground = styled.video`
    ${tw`absolute inset-0 w-full h-full object-cover`}
    pointer-events: none;
`;

const SlideOverlay = styled.div(({ $opacity = 0 }) => [
    tw`absolute inset-0`,
    css`
        background: rgba(0, 0, 0, ${$opacity});
        pointer-events: none;
    `,
]);

/* 轻雾叠层（可留） */
const OpacityOverlay = tw.div`z-10 absolute inset-0 bg-light-500 [opacity:5%] pointer-events-none`;

/* —— 每页内的标题&CTA（跟随滑动） —— */
const SlideCopy = styled.div`
  ${tw`absolute z-20 flex flex-col items-start`}
  left: max(28px, env(safe-area-inset-left));
  bottom: max(60px, calc(env(safe-area-inset-bottom) + 60px));
  max-width: min(920px, 92vw);
  gap: 12px;
`;

const Subcopy = styled.p(({ $overlayOpacity = 0 }) => [
    tw`text-left m-0`,
    css`
        color: #fffbdc;
        font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
        font-weight: 400;
        line-height: 1;
        font-size: 2.5rem;
        white-space: pre-line;
        //text-transform: uppercase;     /* ✅ 全部大写 */
        letter-spacing: -0.04em; /* ✅ 收紧字母间距 */
        text-shadow: 0 1px 0 rgba(0, 0, 0, 0.15), 0 8px 24px rgba(0, 0, 0, 0.25);

        @media (min-width: 768px) {
            font-size: clamp(52px, 6.5vw, 104px);
            font-weight: 400;
            letter-spacing: -0.08em;
        }
    `,
    $overlayOpacity > 0
        ? css`
              display: inline-block;
              padding: 10px;
              background: rgba(0, 0, 0, ${$overlayOpacity});
          `
        : null,
]);

const CTAButton = styled.button`
  ${tw`rounded-full px-6 py-3 text-sm sm:text-base shadow transition duration-300 focus:outline-none focus:shadow-outline`}
  background: #14133B;
  color: #FAB600;
  font-family: "Work Sans", system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
  font-weight: 300;
  letter-spacing: 0.2px;
  border: 1px solid rgba(255,255,255,0.18);
  backdrop-filter: saturate(180%) blur(4px);
  padding-left: clamp(60px, 4vw, 68px);
  padding-right: clamp(60px, 4vw, 68px);
  padding-top: clamp(6px, 2.6vw, 8px);
  padding-bottom: clamp(6px, 2.6vw, 8px);
  &:hover { filter: brightness(1.06); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

/* —— 右上角翻页按钮（PC 显示，手机隐藏）—— */
const Pager = styled.div`
    ${tw`absolute hidden md:flex items-center gap-2`}
    top: max(40px, calc(env(safe-area-inset-top) + 40px));
    right: max(24px, env(safe-area-inset-right));
    z-index: 39;
    opacity: 0;
    pointer-events: none;
    transition: opacity 160ms ease-in-out;

    ${Container}:hover &,
    ${Container}:focus-within & {
        opacity: 1;
        pointer-events: auto;
    }
`;
const HERO_ARROWS = {
    left: {
        default: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/Group%2017.png",
        hover: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/left-arrow%202.png",
    },
    right: {
        default: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/Group%2018.png",
        hover: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/LOGO%20for%20website/grain%20icon/right-arrow%203.png",
    },
};

const IconBtn = styled.button(({ $dir = "left" }) => [
    tw`w-12 h-12 rounded-full focus:outline-none`,
    css`
        border: none;
        cursor: pointer;
        display: inline-flex;
        background-color: transparent;
        background-image: url("${HERO_ARROWS[$dir].default}");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
        transition: background-image 140ms ease-in-out;

        &:hover,
        &:focus-visible {
            background-image: url("${HERO_ARROWS[$dir].hover}");
        }

        &:active {
            background-image: url("${HERO_ARROWS[$dir].hover}");
        }

        &:focus-visible {
            outline: 2px solid rgba(250,182,0,0.8);
            outline-offset: 4px;
        }
    `,
]);

/* === 数据 === */
const ORDER_URL = "https://order.grainmediterranean.com/";

const SLIDES = [
    {
        img: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/1MB%E9%A6%96%E9%A1%B5%E8%BD%AE%E6%92%AD%E5%9B%BE/LONDONHEALTHBOWL.jpg",
        title: "London, Your New Healthy Bowl Is Here.\n",
        cta: "ORDER NOW",
        href: ORDER_URL,
        overlayOpacity: 0.2,
    },
    {
        video: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/oliveoil.mp4",
        title: "Always Avocado Oil\n Always Extra Virgin Olive Oil\n",
        cta: "ORDER NOW",
        href: ORDER_URL,
        overlayOpacity: 0.2,
    },
    {
        img: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/1MB%E9%A6%96%E9%A1%B5%E8%BD%AE%E6%92%AD%E5%9B%BE/_MLP4700.jpg",
        title: "We'd Love You to Join Our Team\n",
        cta: "VIEW OPENINGS",
        href: "/career",
        overlayOpacity: 0.25,
    },
];

export default () => {
    const [idx, setIdx] = useState(0);
    const [snapOff, setSnapOff] = useState(false);
    const [dragging, setDragging] = useState(false);
    const wrapRef = useRef(null);

    // 扩展序列：首尾各克隆一页，无缝 loop
    const EXT = useMemo(() => {
        const n = SLIDES.length;
        if (!n) return [];
        return [SLIDES[n - 1], ...SLIDES, SLIDES[0]];
    }, []);

    const pageRef = useRef(1);
    const isAdjustingRef = useRef(false);
    const byCodeRef = useRef(false);
    const scrollEndTimer = useRef(null);
    const pointerActiveRef = useRef(false);
    const pointerStartXRef = useRef(0);
    const pointerScrollStartRef = useRef(0);
    const pointerIdRef = useRef(null);
    const pointerDeltaRef = useRef(0);

    const handleCTAClick = useCallback((href) => {
        if (!href) return;
        window.location.href = href;
    }, []);

    const getWidth = () => (wrapRef.current ? wrapRef.current.clientWidth : 0);

    const jumpWithoutSnap = useCallback((targetPage) => {
        const el = wrapRef.current;
        if (!el) return;
        isAdjustingRef.current = true;
        setSnapOff(true);
        const w = getWidth();
        requestAnimationFrame(() => {
            el.scrollTo({ left: targetPage * w, behavior: "auto" });
            pageRef.current = targetPage;
            requestAnimationFrame(() => {
                setSnapOff(false);
                requestAnimationFrame(() => { isAdjustingRef.current = false; });
            });
        });
    }, []);

    const jumpSmooth = useCallback((targetPage) => {
        const el = wrapRef.current;
        if (!el) return;
        const w = getWidth();
        byCodeRef.current = true;
        el.scrollTo({ left: targetPage * w, behavior: "smooth" });
        pageRef.current = targetPage;
    }, []);

    const nextPage = useCallback(() => { jumpSmooth(pageRef.current + 1); }, [jumpSmooth]);
    const prevPage = useCallback(() => { jumpSmooth(pageRef.current - 1); }, [jumpSmooth]);

    const calcPageIndex = (scrollLeft, w) => Math.floor((scrollLeft + w * 0.5) / (w || 1));

    const updateIdxFromPage = useCallback((page) => {
        const n = SLIDES.length;
        const realIdx = page === 0 ? n - 1 : (page === n + 1 ? 0 : page - 1);
        setIdx(realIdx);
    }, []);

    const handleScrollEnd = useCallback(() => {
        const el = wrapRef.current;
        if (!el) return;
        scrollEndTimer.current = null;
        const w = getWidth();
        const n = SLIDES.length;
        const i = calcPageIndex(el.scrollLeft, w);

        updateIdxFromPage(i);

        if (i === 0) {
            jumpWithoutSnap(n);
        } else if (i === n + 1) {
            jumpWithoutSnap(1);
        } else {
            pageRef.current = i;
        }
        byCodeRef.current = false;
    }, [jumpWithoutSnap, updateIdxFromPage]);

    const handlePointerDown = useCallback((event) => {
        // Skip if interacting with focusable controls (CTA button etc.)
        const target = event.target;
        if (target && target.closest("button, a, input, textarea, select")) return;
        // Only handle primary mouse button; allow touch devices to keep native swiping
        if ((event.button !== undefined && event.button !== 0) || event.pointerType === "touch") return;
        const el = wrapRef.current;
        if (!el) return;
        pointerActiveRef.current = true;
        pointerStartXRef.current = event.clientX;
        pointerScrollStartRef.current = el.scrollLeft;
        const pointerId = event.pointerId ?? "mouse";
        pointerIdRef.current = pointerId;
        if (event.pointerId != null && el.setPointerCapture) {
            try { el.setPointerCapture(event.pointerId); } catch (err) { /* ignore */ }
        }
        pointerDeltaRef.current = 0;
        setDragging(true);
        setSnapOff(true);
        event.preventDefault();
    }, []);

    const handlePointerMove = useCallback((event) => {
        if (!pointerActiveRef.current) return;
        const pointerId = event.pointerId ?? "mouse";
        if (pointerId !== pointerIdRef.current) return;
        const el = wrapRef.current;
        if (!el) return;
        const dx = event.clientX - pointerStartXRef.current;
        pointerDeltaRef.current = dx;
        el.scrollLeft = pointerScrollStartRef.current - dx;
        event.preventDefault();
    }, []);

    const releasePointer = useCallback((event) => {
        if (!pointerActiveRef.current) return;
        const pointerId = event.pointerId ?? "mouse";
        if (pointerId !== pointerIdRef.current) return;
        pointerActiveRef.current = false;
        pointerIdRef.current = null;
        setDragging(false);
        const el = wrapRef.current;
        if (event.pointerId != null && el && el.releasePointerCapture) {
            try { el.releasePointerCapture(event.pointerId); } catch (err) { /* ignore */ }
        }
        setSnapOff(false);
        if (scrollEndTimer.current) {
            clearTimeout(scrollEndTimer.current);
            scrollEndTimer.current = null;
        }
        const w = getWidth();
        const delta = pointerDeltaRef.current;
        pointerDeltaRef.current = 0;
        const threshold = Math.min(w * 0.25, 180);
        if (Math.abs(delta) > threshold) {
            if (delta < 0) {
                nextPage();
            } else {
                prevPage();
            }
        } else if (el) {
            jumpSmooth(pageRef.current);
        }
    }, [getWidth, jumpSmooth, nextPage, prevPage]);

    const handlePointerUp = useCallback((event) => {
        releasePointer(event);
    }, [releasePointer]);

    const handlePointerCancel = useCallback((event) => {
        releasePointer(event);
    }, [releasePointer]);

    const onScroll = useCallback(() => {
        if (isAdjustingRef.current) return;
        if (scrollEndTimer.current) clearTimeout(scrollEndTimer.current);
        scrollEndTimer.current = setTimeout(() => { handleScrollEnd(); }, 80);
    }, [handleScrollEnd]);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        jumpWithoutSnap(1);
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => { el && el.removeEventListener("scroll", onScroll); };
    }, [jumpWithoutSnap, onScroll]);

    useEffect(() => {
        const onResize = () => { jumpWithoutSnap(pageRef.current); };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [jumpWithoutSnap]);

    return (
        <Container>
            {/* —— 背景轮播（含首尾克隆） —— */}
            <SlidesWrap
                ref={wrapRef}
                $snapOff={snapOff}
                $dragging={dragging}
                aria-label="Background carousel"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onPointerLeave={handlePointerCancel}
            >
                {EXT.map((s, i) => (
                    <Slide
                        key={`ext-${i}`}
                        $img={s.img}
                        role="group"
                        aria-roledescription="slide"
                        aria-label={`${i + 1} of ${EXT.length}`}
                        aria-hidden={i === 0 || i === EXT.length - 1 ? "true" : undefined}
                        data-clone={i === 0 || i === EXT.length - 1 ? "true" : "false"}
                    >
                        {s.video ? (
                            <VideoBackground
                                src={s.video}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="auto"
                                aria-hidden="true"
                            />
                        ) : null}
                        {s.overlayOpacity ? <SlideOverlay $opacity={s.overlayOpacity} /> : null}
                        {/* ✅ 标题和按钮跟随每张 Slide 一起滑动 */}
                        <SlideCopy>
                            <Subcopy $overlayOpacity={s.subcopyOverlayOpacity}>{s.title}</Subcopy>
                            <CTAButton onClick={() => handleCTAClick(s.href)}>
                                {s.cta || "ORDER NOW"}
                            </CTAButton>
                        </SlideCopy>
                    </Slide>
                ))}
            </SlidesWrap>

            <OpacityOverlay />

            {/* —— 右上角翻页按钮（桌面显示） —— */}
            <Pager>
                <IconBtn aria-label="Previous slide" onClick={prevPage} $dir="left" />
                <IconBtn aria-label="Next slide" onClick={nextPage} $dir="right" />
            </Pager>
        </Container>
    );
};
